import { memo, useCallback, useMemo } from 'react';
import { Badge, Button, Card } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { studioDescriptionKey } from '../../utils/catalogText';
import { ChevronDown, ChevronRight, Eye, Pencil } from 'lucide-react';

import { PERMISSIONS } from '../../constants/permissions';
import { buildDraft } from '../../utils/permissionTree';
import StudioIcon from '../member-studios/StudioIcon';
import Can from '../guards/Can';
import { PermissionTree } from '../shared/PermissionTree';
import RoleStudioEditor from './RoleStudioEditor';

/**
 * One studio of the role's Permissions tab, like the member Studios rows: icon, name, a
 * "N granted" chip, description and, on the right, Edit (custom role, manage permission) or
 * "View only" (system role) and the expand chevron. Below it the read-only tree or, while
 * editing, the editor.
 *
 * @param {object} props
 * @param {object} props.studio Catalog studio with its `tree`.
 * @param {object[]} props.grants The role's `{ permission_id, level }` grants in this studio.
 * @param {boolean} props.isSystem The role is read only.
 * @param {boolean} props.isOpen
 * @param {boolean} props.isEditing
 * @param {(studioId: string) => void} props.onToggleOpen
 * @param {(studioId: string) => void} props.onEdit
 * @param {() => void} props.onStopEditing
 * @param {(studio: object, grants: object[]) => Promise<boolean>} props.onSave
 * @param {(studio: object) => void} props.onRemoveAccess
 */
const RoleStudioCard = ({ studio, grants, isSystem, isOpen, isEditing, onToggleOpen, onEdit, onStopEditing, onSave, onRemoveAccess }) => {
  const { t } = useTranslation('people-access');
  const draft = useMemo(() => buildDraft(studio.tree, grants), [studio.tree, grants]);

  const handleToggle = useCallback(() => onToggleOpen(studio.id), [onToggleOpen, studio.id]);
  const handleChevron = useCallback(
    (event) => {
      event.stopPropagation();
      onToggleOpen(studio.id);
    },
    [onToggleOpen, studio.id],
  );
  const handleEdit = useCallback(
    (event) => {
      event.stopPropagation();
      onEdit(studio.id);
    },
    [onEdit, studio.id],
  );

  const viewOnly = (
    <span className="inline-flex items-center gap-1 text-xs text-content-muted">
      <Eye size={14} aria-hidden />
      {t('roles.detail.viewOnly')}
    </span>
  );
  const editButton = (
    <Can permission={PERMISSIONS.ROLES_MANAGE}>
      <Button variant="secondary" size="sm" leftIcon={Pencil} onClick={handleEdit}>
        {t('roles.detail.permissions.edit')}
      </Button>
    </Can>
  );

  return (
    <Card className="p-4" data-testid={`role-studio-${studio.id}`}>
      {/* A mouse shortcut: the chevron button is the accessible control, so no role here. */}
      <div className="flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-2" onClick={handleToggle}>
        <StudioIcon icon={studio.icon} />
        <div className="min-w-0 flex-1 basis-40">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-content">{studio.name}</h3>
            {grants.length ? (
              <Badge size="small" variant="neutral">
                {t('roles.detail.permissions.studioGranted', { count: grants.length })}
              </Badge>
            ) : null}
          </div>
          <p className="truncate text-xs text-content-muted">{t(studioDescriptionKey(studio.id), { defaultValue: studio.description })}</p>
        </div>
        <div className="flex w-full flex-wrap items-center justify-start gap-2 sm:w-auto sm:shrink-0 sm:justify-end">
          {isSystem ? viewOnly : null}
          {!isSystem && !isEditing ? editButton : null}
          <Button
            variant="ghost"
            size="sm"
            leftIcon={isOpen ? ChevronDown : ChevronRight}
            aria-expanded={isOpen}
            aria-label={t(isOpen ? 'roles.detail.permissions.collapse' : 'roles.detail.permissions.expand', { name: studio.name })}
            onClick={handleChevron}
          />
        </div>
      </div>
      {isOpen ? (
        <div className="mt-4 border-t border-edge pt-4">
          {isEditing ? (
            <RoleStudioEditor
              studio={studio}
              grants={grants}
              onSave={onSave}
              onClose={onStopEditing}
              onRemoveAccess={onRemoveAccess}
            />
          ) : (
            <PermissionTree nodes={studio.tree} draft={draft} mode="read" expand="top" />
          )}
        </div>
      ) : null}
    </Card>
  );
};

export default memo(RoleStudioCard);
